import type { TFunction } from "i18next";
import { isApiError } from "../../../api/client.js";
import { verificationCooldownUnlockDelay } from "./add-phone-wizard-state.js";

export function verificationCooldownCopy(
  t: TFunction,
  input: {
    retryAfter: string;
    retryAfterSource?: string | null;
    language: string;
  },
): string {
  const time = formatVerificationDeadline(input.retryAfter, input.language);
  if (input.retryAfterSource === "provider") {
    return t("whatsapp.addPhone.errors.rateLimitedProvider", { time });
  }
  return t("whatsapp.addPhone.errors.rateLimitedApplication", { time });
}

/** Clock time of the deadline, with the date when it is not today. */
export function formatVerificationDeadline(
  iso: string,
  language: string,
  nowMs: number = Date.now(),
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const sameDay = new Date(nowMs).toDateString() === date.toDateString();
  return new Intl.DateTimeFormat(language.startsWith("he") ? "he-IL" : "en-US", {
    ...(sameDay ? {} : { month: "short", day: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

/** A rate limit with a deadline still in the future. The live countdown explains it. */
export function isVerificationCooldownRateLimit(error: unknown, nowMs = Date.now()): boolean {
  if (!isApiError(error) || error.code !== "WHATSAPP_VERIFICATION_RATE_LIMITED") return false;
  const retryAfter = error.details?.retryAfter;
  if (!retryAfter) return false;
  return verificationCooldownUnlockDelay(retryAfter, nowMs) !== null;
}

export function mapAddPhoneError(
  t: TFunction,
  error: unknown,
  unreachableMessage: string,
  language = "en",
  nowMs = Date.now(),
): string {
  if (isApiError(error)) {
    if (error.status === 0) return unreachableMessage;
    switch (error.code) {
      case "WHATSAPP_VERIFICATION_CODE_INVALID":
        return t("whatsapp.addPhone.errors.invalidCode");
      case "WHATSAPP_VERIFICATION_CODE_EXPIRED":
        return t("whatsapp.addPhone.errors.expiredCode");
      case "WHATSAPP_VERIFICATION_RATE_LIMITED": {
        const retryAfter = error.details?.retryAfter;
        if (retryAfter && verificationCooldownUnlockDelay(retryAfter, nowMs) !== null) {
          return verificationCooldownCopy(t, {
            retryAfter,
            retryAfterSource: error.details?.retryAfterSource,
            language,
          });
        }
        return t("whatsapp.addPhone.errors.rateLimited");
      }
      case "WHATSAPP_VERIFICATION_REQUEST_FAILED":
        return t("whatsapp.addPhone.errors.requestFailed");
      case "WHATSAPP_PIN_MISMATCH":
      case "WHATSAPP_REGISTRATION_FAILED":
        return t("whatsapp.addPhone.errors.registrationFailed");
      case "WHATSAPP_REGISTRATION_PENDING":
        return t("whatsapp.addPhone.errors.stillPending");
      case "WHATSAPP_REGISTRATION_RATE_LIMITED":
        return t("whatsapp.addPhone.errors.registrationRateLimited");
      case "WHATSAPP_DISPLAY_NAME_NOT_APPROVED":
        return t("whatsapp.addPhone.errors.displayNamePending");
      case "WHATSAPP_PHONE_PROGRAMMATIC_ADD_NOT_AVAILABLE":
        return t("whatsapp.addPhone.errors.programmaticAddUnavailable");
      case "WHATSAPP_PHONE_LIMIT_REACHED":
        return t("whatsapp.addPhone.errors.phoneLimitReached");
      case "WHATSAPP_PHONE_ALREADY_CONNECTED":
        return t("whatsapp.addPhone.errors.alreadyConnected");
      case "WHATSAPP_PHONE_OWNED_BY_ANOTHER_ORGANIZATION":
        return t("whatsapp.addPhone.errors.ownedByAnotherOrganization");
      case "WHATSAPP_WABA_OWNED_BY_ANOTHER_ORGANIZATION":
        return t("whatsapp.meta.wabaOwnedByAnotherOrganization");
      case "WHATSAPP_VERIFICATION_ALREADY_COMPLETE":
        return t("whatsapp.addPhone.errors.verificationAlreadyComplete");
      case "WHATSAPP_PHONE_ACTIVE_ELSEWHERE":
        return t("whatsapp.addPhone.errors.activeElsewhere");
      case "WHATSAPP_PHONE_NOT_IN_WABA":
        return t("whatsapp.addPhone.errors.notInWaba");
      case "WHATSAPP_CREDENTIALS_INVALID":
        return t("whatsapp.addPhone.errors.credentialsInvalid");
      case "WHATSAPP_NOT_CONNECTED":
        return t("whatsapp.addPhone.errors.notConnected");
      case "WHATSAPP_PHONE_ALREADY_EXISTS":
        return t("whatsapp.addPhone.errors.phoneAlreadyExists");
      case "WHATSAPP_EMBEDDED_SIGNUP_VERIFICATION_INCOMPLETE":
        return t("whatsapp.meta.verificationIncomplete");
      case "WHATSAPP_PROVIDER_ERROR":
        return t("whatsapp.addPhone.errors.provider");
      case "VALIDATION_FAILED":
        return t("whatsapp.addPhone.errors.validation");
      case "FORBIDDEN":
        return t("whatsapp.addPhone.errors.forbidden");
      default:
        return t("whatsapp.addPhone.errors.generic");
    }
  }
  return unreachableMessage;
}
